import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"

export interface EmailMessage {
  id: string
  email_account_id: string
  message_id: string
  thread_id: string | null
  direction: "inbound" | "outbound"
  folder: string
  from_addresses: Array<{ name: string | null; email: string }>
  to_addresses: Array<{ name: string | null; email: string }>
  cc_addresses: Array<{ name: string | null; email: string }>
  subject: string | null
  body_text: string | null
  body_html: string | null
  is_read: boolean
  is_starred: boolean
  has_attachments: boolean
  date: string
  attachments: Array<{
    id: string
    filename: string
    content_type: string | null
    size_bytes: number | null
  }>
  entity_links: Array<{
    id: string
    entity_type: string
    entity_id: string
    entity_name?: string
    link_type: string
  }>
  // Tracking fields (outbound emails)
  open_count?: number
  opened_at?: string | null
  click_count?: number
  clicked_at?: string | null
  tracking_id?: string | null
  created_at: string
}

interface EmailFilters {
  email_account_id?: string
  folder?: string
  direction?: string
  search?: string
  is_read?: boolean
  cursor?: string
}

function buildQuery(filters: EmailFilters): string {
  const params = new URLSearchParams()
  if (filters.email_account_id) params.set("email_account_id", filters.email_account_id)
  if (filters.folder) params.set("folder", filters.folder)
  if (filters.direction) params.set("direction", filters.direction)
  if (filters.search) params.set("search", filters.search)
  if (filters.is_read !== undefined) params.set("is_read", String(filters.is_read))
  if (filters.cursor) params.set("cursor", filters.cursor)
  const q = params.toString()
  return q ? `?${q}` : ""
}

export function useEmails(filters: EmailFilters = {}) {
  return useQuery({
    queryKey: ["emails", filters],
    queryFn: () =>
      api.get<{ data: EmailMessage[]; meta: { has_more: boolean; next_cursor: string | null } }>(
        `/api/v1/emails${buildQuery(filters)}`
      ),
    refetchInterval: 120_000, // 2 minutes
  })
}

export function useEmail(id: string | undefined) {
  return useQuery({
    queryKey: ["emails", id],
    queryFn: () => api.get<{ data: EmailMessage }>(`/api/v1/emails/${id}`),
    enabled: !!id,
  })
}

export function useEmailThread(id: string | undefined) {
  return useQuery({
    queryKey: ["emails", id, "thread"],
    queryFn: () => api.get<{ data: EmailMessage[] }>(`/api/v1/emails/${id}/thread`),
    enabled: !!id,
  })
}

export function useMarkEmailRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/emails/${id}/read`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["emails"] })
    },
  })
}

export function useToggleEmailStar() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/emails/${id}/star`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["emails"] })
    },
  })
}

export function useAddEmailLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ emailId, data }: { emailId: string; data: { entity_type: string; entity_id: string } }) =>
      api.post(`/api/v1/emails/${emailId}/links`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["emails"] })
    },
  })
}

export function useRemoveEmailLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ emailId, linkId }: { emailId: string; linkId: string }) =>
      api.delete(`/api/v1/emails/${emailId}/links/${linkId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["emails"] })
    },
  })
}

// ── Compose & Send ──────────────────────────────────────────────

export interface ComposeEmailData {
  email_account_id: string
  to: string[]
  cc?: string[]
  bcc?: string[]
  subject: string
  body_html: string
  body_text?: string
  is_draft?: boolean
  scheduled_at?: string
}

export interface ReplyEmailData {
  body_html: string
  body_text?: string
  reply_all?: boolean
  email_account_id?: string
}

export interface ForwardEmailData {
  to: string[]
  cc?: string[]
  body_html: string
  body_text?: string
  email_account_id?: string
}

export function useComposeEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ComposeEmailData) =>
      api.post<{ data: EmailMessage }>("/api/v1/emails", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["emails"] })
    },
  })
}

export function useSendEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: EmailMessage }>(`/api/v1/emails/${id}/send_email`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["emails"] })
    },
  })
}

export function useReplyEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReplyEmailData }) =>
      api.post<{ data: EmailMessage }>(`/api/v1/emails/${id}/reply`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["emails"] })
    },
  })
}

export function useForwardEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ForwardEmailData }) =>
      api.post<{ data: EmailMessage }>(`/api/v1/emails/${id}/forward`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["emails"] })
    },
  })
}

export function useUpdateDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ComposeEmailData> }) =>
      api.patch<{ data: EmailMessage }>(`/api/v1/emails/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["emails"] })
    },
  })
}

// ── Entity Search & Linking ─────────────────────────────────────

export interface EntitySearchResult {
  entity_type: string
  entity_id: string
  label: string
  sublabel: string | null
}

export function useEntitySearch(query: string, types = "Contact,Company,Deal") {
  return useQuery({
    queryKey: ["entity_search", query, types],
    queryFn: () =>
      api.get<{ data: EntitySearchResult[] }>(
        `/api/v1/emails/search_entities?q=${encodeURIComponent(query)}&types=${types}&limit=10`
      ),
    enabled: query.length >= 2,
  })
}
